import {
  signInWithRedirect,
  signIn as amplifySignIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
} from 'aws-amplify/auth';

/**
 * Sign in with Google OAuth
 * Redirects user to Google sign-in via Cognito
 */
export async function loginWithGoogle(): Promise<void> {
  await signInWithRedirect({ provider: 'Google' });
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<void> {
  const signInInput: SignInInput = {
    username: email,
    password,
  };
  await amplifySignIn(signInInput);
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email: string, password: string): Promise<void> {
  const signUpInput: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
      },
    },
  };
  await signUp(signUpInput);
}

/**
 * Confirm sign up with verification code
 */
export async function confirmSignUpCode(email: string, code: string): Promise<void> {
  const confirmInput: ConfirmSignUpInput = {
    username: email,
    confirmationCode: code,
  };
  await confirmSignUp(confirmInput);
}

/**
 * Sign out current user
 * Redirects to sign-out URL configured in Amplify
 */
export async function logout(): Promise<void> {
  await signOut();
}

/**
 * Get JWT ID token for API requests
 * @throws Error if token is not available or session is invalid
 */
export async function getIdToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    return token;
  } catch (error) {
    console.error('Error getting ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
}

/**
 * Get current user info
 */
export async function getCurrentUserInfo() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}
